/**
 * Hero block at the top of the Students tab.
 *
 * Two sections separated by a horizontal rule:
 *   1. "Patterns to be aware of"   — gaps with current_count >= 5
 *   2. "What's improving"           — gaps that dropped below 5 in last 5–7 days
 *
 * Per spec §3:
 *   - Each pattern shows: bold gap name · colored count · "since {date}"
 *   - Sub-line: core_misconception (observable behavior)
 *   - Dot color: red for ≥14 days old, amber for newer
 *   - Improving section hidden entirely if empty
 *   - Patterns section shows muted "No class-wide patterns this week" if empty
 *
 * No teaching suggestion. No prescriptive language.
 */

import { useMemo } from 'react';

function formatSinceDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `since ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

function patternDotColor(firstSeenIso) {
  if (!firstSeenIso) return 'bg-teacher-amber';
  const days = (Date.now() - new Date(firstSeenIso).getTime()) / 86400000;
  return days >= 14 ? 'bg-teacher-red' : 'bg-teacher-amber';
}

function countColor(firstSeenIso) {
  if (!firstSeenIso) return 'text-teacher-amber';
  const days = (Date.now() - new Date(firstSeenIso).getTime()) / 86400000;
  return days >= 14 ? 'text-teacher-red' : 'text-teacher-amber';
}

export default function ClassroomSignalHero({ signal, lastComputedAt }) {
  const patterns = signal?.patterns || [];
  const improving = signal?.improving || [];

  const refreshedLabel = useMemo(() => {
    if (!lastComputedAt) return null;
    const d = new Date(lastComputedAt);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  }, [lastComputedAt]);

  // Stale check: if signal hasn't been computed in >24h, surface to teacher
  // per plan §4.7 ("Class signal hasn't refreshed today — last update {age}").
  // The cron is supposed to run nightly; this hint catches cron failures
  // before patterns get silently stale.
  const isStale = useMemo(() => {
    if (!lastComputedAt) return signal === null; // missing entirely → stale
    const ageMs = Date.now() - new Date(lastComputedAt).getTime();
    return ageMs > 30 * 60 * 60 * 1000; // 30h cushion past the nightly cycle
  }, [lastComputedAt, signal]);

  return (
    <div className="bg-white rounded-xl border border-teacher-border p-5 relative overflow-hidden">
      {/* Brand-gradient left accent (per spec §10.1) */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-gradient" />

      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider font-medium bg-brand-gradient bg-clip-text text-transparent">
          Classroom signal · Last 7 days
        </div>
        {isStale && (
          <div className="text-[11px] text-teacher-amber bg-teacher-amber-light border border-teacher-amber/30 px-2 py-0.5 rounded-full">
            Class signal hasn't refreshed today — last update {refreshedLabel || '—'}
          </div>
        )}
      </div>

      {/* Patterns section */}
      <div className="text-[10px] uppercase tracking-wider font-medium text-teacher-text-tertiary mb-2">
        Patterns to be aware of
      </div>
      {patterns.length === 0 ? (
        <div className="text-xs text-teacher-text-tertiary mb-4">
          No class-wide patterns this week.
        </div>
      ) : (
        <ul className="space-y-2 mb-4">
          {patterns.map(p => (
            <li key={p.atlas_node_id} className="flex gap-2.5 items-baseline text-[13px] leading-snug">
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 mt-[5px] inline-block ${patternDotColor(p.first_seen_date)}`}
              />
              <span>
                <span>
                  <strong className="text-teacher-text-primary">{p.node_name}</strong>
                  <span className="text-teacher-text-tertiary"> · </span>
                  <span className={`font-medium ${countColor(p.first_seen_date)}`}>
                    {p.current_count} {p.current_count === 1 ? 'student' : 'students'}
                  </span>
                  <span className="text-teacher-text-tertiary"> · </span>
                  <span className="text-teacher-text-secondary">{formatSinceDate(p.first_seen_date)}</span>
                </span>
                {p.core_misconception && (
                  <div className="text-xs text-teacher-text-tertiary mt-0.5">
                    {p.core_misconception}
                  </div>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* What's improving — hidden entirely when empty */}
      {improving.length > 0 && (
        <>
          <div className="h-px bg-teacher-border my-4" />
          <div className="text-[10px] uppercase tracking-wider font-medium bg-wins-gradient bg-clip-text text-transparent mb-2">
            What's improving
          </div>
          <ul className="space-y-1.5">
            {improving.map(i => (
              <li key={i.atlas_node_id} className="flex gap-2.5 items-baseline text-[13px] leading-snug">
                <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-[5px] inline-block bg-teacher-green" />
                <span>
                  <strong className="text-teacher-text-primary">{i.node_name}</strong>
                  <span className="text-teacher-text-tertiary"> · </span>
                  <span className="text-teacher-green font-medium">no longer a class-wide pattern</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {refreshedLabel && (
        <div className="mt-4 pt-3 border-t border-teacher-border text-[10px] text-teacher-text-tertiary">
          Class signal refreshed {refreshedLabel}
        </div>
      )}
    </div>
  );
}
